import { Box } from "@chakra-ui/react";
import _ from "lodash";
import { isNil } from "lodash/fp";
import { useTranslation } from "next-i18next";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import * as Yup from "yup";

import Spacer from "components/Spacer";
import { FixedButtonsWrapper, OFButton } from "design_system/components/OFButton";
import { OFText } from "design_system/components/OFText";
import {
  RegionType,
  StripePriceDto,
  StripePriceType,
  StripeProductFilterV2,
  ViewerDto
} from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { colors } from "src/colors";
import { productVariants } from "src/configs/productVariants";
import { CURRENT_COUNTRY } from "src/constants/Countries";
import { useYupValidationResolver } from "src/hooks/useYup";
import { EvalFlowPriceSelectionVariant } from "src/types";
import getNameProp from "src/utils/getNameProp";
import { formatPrice } from "src/utils/priceFormat";
import TrackingWrapper from "src/wrappers/TrackingWrapper";

import FAQSection from "./FAQSection";
import { Variation } from "./Variation";
import {
  checkIsFixedVariant,
  checkPriceMetaMatchPreSelectMeta,
  getBasePricePerBoxFromVariants
} from "./utils";

interface PriceSelectionInterface {
  productId: string;
  emitSelectedPriceId?: (priceId: string) => Promise<void>;
  onlyShowOTC?: boolean;
  autoProcess?: boolean;
  onConfirm?: (priceId: string) => void;
  isLoading?: boolean;
  preselect?: string;
  displayPrice?: boolean;
  isShowFAQ?: boolean;
  confirmBtnText?: string;
  selectedVariant?: EvalFlowPriceSelectionVariant;
  dosageAnswer?: number;
}

const defaultVariants = ["dosage", "frequency"];

const query = `
query getStripeProductDetail($input: StripeProductFilterV2) {
  viewer {
    user {
      stripeProducts(input: $input) {
        id
        texts {
          benefits
          unit_patient
        }
        metadata
        prices {
          id
          active
          type
          unit_amount
          unit_amount_decimal
          metadata {
            isArchived
            frequency
            dosage
            boxes
            frequency_quantity_display
          }
          texts {
            actual_price_display
            frequency
            frequency_popular
            frequency_display
            frequency_actual_price_display
            dosage
            dosage_popular
            dosage_display
          }
        }
      }
    }
  }
}

`;

export const PriceSelection: FC<PriceSelectionInterface> = ({
  productId,
  onlyShowOTC = false,
  emitSelectedPriceId,
  autoProcess = true,
  onConfirm,
  isLoading,
  preselect,
  displayPrice = false,
  isShowFAQ = true,
  confirmBtnText = "",
  selectedVariant,
  dosageAnswer
}) => {
  const { t } = useTranslation(["price_selection", "evaluation_intro"]);
  const { data: stripeProduct, isLoading: isLoadingProduct } = useQuery(
    ["getStripeProductDetail", productId],
    async () => {
      const { viewer } = await graphQLClient.request<
        { viewer: ViewerDto },
        {
          input: StripeProductFilterV2;
        }
      >(query, {
        input: {
          id: productId
        }
      });

      return viewer?.user?.stripeProducts?.[0];
    },
    {
      // enabled: !isNil(productId)
    }
  );
  const isAllPricesOTC = useMemo(() => {
    return stripeProduct?.prices.every((price) => price.type === StripePriceType.OneTime);
  }, [stripeProduct]);

  const [variantIndex, setVariantIndex] = useState(0);
  const [selectedValues, setSelectedValues] = useState({});
  const [paymentAmount, setPaymentAmount] = useState("");
  const [defaultVal, setDefaultVal] = useState(null);

  const variants = productVariants[productId] || defaultVariants;
  const variant = selectedVariant || variants[variantIndex];
  const filteredPriceType = useMemo(() => {
    return onlyShowOTC || isAllPricesOTC ? StripePriceType.OneTime : StripePriceType.Recurring;
  }, [onlyShowOTC, isAllPricesOTC]);

  const isLastVariant = variantIndex === variants.length - 1;
  const requiredPrescription = stripeProduct?.metadata?.requires_prescription;
  let preselectPrice = null;

  if (preselect) {
    preselectPrice = stripeProduct?.prices.find((price) => price.id === preselect);
    if (preselectPrice) {
      preselectPrice = {
        ...preselectPrice,
        metadata: { ...preselectPrice.metadata, ...preselectPrice.texts }
      };
    }
  }

  let filteredPricesIds: StripePriceDto[] = useMemo(() => {
    return stripeProduct?.prices.reduce((prev, cur) => {
      if (cur.type !== filteredPriceType || !cur.active || cur.metadata?.isArchived === "true") {
        return prev;
      }

      const isMatchPreselecPriceMeta = selectedVariant
        ? checkPriceMetaMatchPreSelectMeta(cur, preselectPrice, selectedVariant)
        : null;

      if (isMatchPreselecPriceMeta !== null && !isMatchPreselecPriceMeta) {
        return prev;
      }

      return [...prev, { ...cur, metadata: { ...cur.texts, ...cur.metadata } }];
    }, []);
  }, [filteredPriceType, preselectPrice, stripeProduct?.prices, selectedVariant]);

  for (let i = 0; i < variantIndex; i++) {
    if (selectedValues[variants[i]]) {
      filteredPricesIds = filteredPricesIds.filter(
        (price) => price.metadata[variants[i]] === selectedValues[variants[i]]
      );
    }
  }

  filteredPricesIds = _.orderBy(filteredPricesIds, (item) => +_.get(item, `metadata.${variant}`), [
    "desc"
  ]);

  const uniquePricesByVariants = _.uniqBy(
    filteredPricesIds.filter((d) => {
      return !!d.metadata[variant];
    }),
    `metadata.${variant}`
  );
  const basePricePerBox =
    variant === "frequency" ? getBasePricePerBoxFromVariants(uniquePricesByVariants) : null;

  const validationSchema = Yup.object({
    variant_selection: Yup.string().required("At least one product is required")
  });

  const isFixedMonthlyDosage = useMemo(
    () => checkIsFixedVariant(stripeProduct?.prices, "dosage"),
    [stripeProduct]
  );

  const btnText = useMemo(() => {
    return confirmBtnText || (displayPrice && !isNil(paymentAmount) && isLastVariant)
      ? t("price_selection:confirm-price-message", { amount: paymentAmount })
      : t("price_selection:confirm-button-text");
  }, [confirmBtnText, displayPrice, paymentAmount, t, isLastVariant]);

  const heading = useMemo(() => {
    let type = "default";
    const options = { defaultValue: "", numOfMonths: 0, numOfTimes: 0, numOfTabs: 0 };

    if (variant === "frequency") {
      if (!isFixedMonthlyDosage) {
        const mostPopularPrice = uniquePricesByVariants.find(
          (currentPrice) => currentPrice.metadata.frequency_popular === "true"
        );

        const mostPopularFreq = (mostPopularPrice?.metadata?.frequency || 3).toString();

        type = "based_on_dose";
        options.numOfMonths = parseInt(mostPopularFreq, 10);
      }
    } else if (variant === "dosage") {
      if (dosageAnswer) {
        type = "based_on_eval";
        options.numOfTimes = dosageAnswer;
        options.numOfTabs = dosageAnswer;
      }
    }

    return t(`price_selection:headings.${variant}.${type}`, options);
  }, [t, uniquePricesByVariants, variant, isFixedMonthlyDosage, dosageAnswer]);

  const chooseYourText = useMemo(() => {
    if (variant === "frequency" && isFixedMonthlyDosage) {
      return "";
    }

    return t(`price_selection:choose_your.${variant}`, { defaultValue: "" });
  }, [variant, isFixedMonthlyDosage, t]);

  const resolver = useYupValidationResolver(validationSchema);

  const { handleSubmit, setValue, watch } = useForm({
    resolver,
    mode: "onChange",
    defaultValues: {
      variant_selection: defaultVal
    }
  });

  const price = watch("variant_selection", defaultVal);

  /*
   * Functions
   */

  const onNextVariant = (values) => {
    // has selectedVariant -> treatment summary -> don't need to go next step
    if (selectedVariant && onConfirm) {
      onConfirm(values.variant_selection);
      return;
    }

    setSelectedValues({
      ...selectedValues,
      [variant]: filteredPricesIds.find((p) => p.id === values.variant_selection).metadata[variant]
    });

    if (!isLastVariant) {
      setVariantIndex((d) => d + 1);
    } else {
      if (emitSelectedPriceId) emitSelectedPriceId(values.variant_selection);
      if (onConfirm) onConfirm(values.variant_selection);
    }
  };

  const renderFAQSection = (): JSX.Element | null => {
    return isLastVariant ? (
      filteredPriceType === StripePriceType.Recurring ? (
        CURRENT_COUNTRY === RegionType.Jp ? (
          <FAQSection t={t} type="shipping" />
        ) : (
          <FAQSection t={t} type="frequency" />
        )
      ) : null
    ) : (
      <></>
    );
  };

  useEffect(() => {
    const amount = filteredPricesIds?.find((p) => p.id === price)?.unit_amount;

    setPaymentAmount(isNil(amount) ? null : formatPrice(amount));
  }, [price, filteredPricesIds, t]);

  useEffect(() => {
    if (isLoadingProduct) {
      return;
    }
    // if unique prices filtered by variants are empty or less than or equal to 1, skip the dosage and display price selection as users do not need to choose dosage
    if (uniquePricesByVariants.length <= 1) {
      setVariantIndex(1);
    }

    const tempDefaultVal = !preselectPrice
      ? uniquePricesByVariants.find((p) => p.metadata[`${variant}_popular`] === "true")?.id
      : filteredPricesIds.find((currentPrice) => currentPrice.id === preselectPrice.id)?.id;

    setDefaultVal(tempDefaultVal);
  }, [uniquePricesByVariants, filteredPricesIds, preselectPrice, variant, isLoadingProduct]);

  useEffect(() => {
    setValue("variant_selection", defaultVal, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultVal]);

  return (
    <TrackingWrapper>
      <Box {...getNameProp("price-selection-container")} maxWidth="800px" w="100%">
        <OFText textStyle="subhead1" color={colors.orange[400]} textTransform="uppercase" mb={6}>
          {t(`price_selection:variant_heading.${variant}`, { defaultValue: "" })}
        </OFText>

        <OFText textStyle="h4" {...getNameProp("price-selection-title")} whiteSpace="pre-line">
          {heading}
        </OFText>

        <Spacer y="12" />

        <OFText textStyle="body3" mb={4} {...getNameProp("price-selection-body")}>
          {chooseYourText}
        </OFText>

        <form onSubmit={handleSubmit(onNextVariant)}>
          <Box {...getNameProp("price-selection-variation-form")}>
            {uniquePricesByVariants.map((currentPrice) => (
              <Variation
                key={currentPrice.id}
                variant={variant}
                price={currentPrice}
                isChecked={watch("variant_selection") === currentPrice.id}
                setValue={setValue}
                product={stripeProduct}
                basePricePerBox={basePricePerBox}
              />
            ))}
          </Box>

          <Spacer y="12" />

          {!autoProcess && (
            <FixedButtonsWrapper>
              <OFButton
                type="submit"
                disabled={!price}
                isLoading={isLoading}
                w={["100%", null, "max-content"]}
                {...getNameProp("price-selection-form-button")}
              >
                {btnText}
              </OFButton>
            </FixedButtonsWrapper>
          )}
        </form>

        {requiredPrescription && isShowFAQ && renderFAQSection()}
      </Box>
    </TrackingWrapper>
  );
};
